/**
 * Live scherm: groot scorebord, status, serveerder, grote knoppen,
 * correcties, kantwissel en debug/simulatie. Bruikbaar met natte vingers.
 */

import { h } from "./dom";
import type { App } from "./app";
import { manualEv, undoEv } from "../detect/simulated";
import { sideSwitchPending, statusInfo, servingDisplay } from "./status";
import { buildDebugPanel } from "./debugPanel";
import { openSetConfirm } from "./setConfirm";
import { getSideSwitchStatus, type MatchState } from "../core";

export function renderLive(app: App, root: HTMLElement): void {
  const st = app.engine.state;
  const teams = st.config.teams;
  const status = statusInfo(st);
  const serve = servingDisplay(st);
  const pendingSwitch = sideSwitchPending(st);
  const setInfo = `Set ${Math.max(1, st.setNumber)} — ${st.setNumber >= 3 ? "beslissende set tot " + st.config.setTargets[2] : "tot " + st.config.setTargets[Math.min(1, st.setNumber - 1)]}`;

  const scoreEl = h("div", { class: "scorecard" }, [
    teamRow(st, "A"),
    h("div", { class: "score-middle" }, [
      h("div", { class: "score-big" }, [String(st.currentSet.points.A)]),
      h("div", { class: "dash" }, ["—"]),
      h("div", { class: "score-big" }, [String(st.currentSet.points.B)]),
    ]),
    teamRow(st, "B"),
  ]);

  const statusChip = h("span", { class: `chip chip-${status.tone}` }, [status.label]);

  const serverLine = serve
    ? h("div", { class: "server-line" }, [
        h("span", { class: "muted" }, ["Serveert: "]),
        h("strong", {}, [`${serve.player} `]),
        h("span", { class: "muted" }, [`(${serve.teamLabel}, ${serve.note}, ${serve.sideLabel} van de camera)`]),
      ])
    : h("div", { class: "server-line muted" }, ["Nog geen opslag gedetecteerd"]);

  const switchLine =
    st.phase === "in_play" || st.phase === "awaiting_first_serve" || st.phase === "set_over"
      ? h("div", { class: "muted small" }, [
          `Kantwissel: ${getSideSwitchStatus(st).pending > 0 ? `${getSideSwitchStatus(st).pending} aan de beurt (volgende bij totaal ${getSideSwitchStatus(st).nextThreshold})` : `geen pending (volgende bij totaal ${getSideSwitchStatus(st).nextThreshold})`}`,
        ])
      : null;

  const buttons: HTMLElement[] = [
    h("div", { class: "btn-big-row" }, [
      pointBtn(app, "A"),
      pointBtn(app, "B"),
    ]),
  ];

  const extras: HTMLElement[] = [];
  const r = st.rally;
  if (r?.result?.status === "proposal") {
    extras.push(
      h("button", {
        class: "btn warn big",
        onclick: () => app.emit({ type: "RALLY_RESULT_CONFIRMED", detectorId: r.detectorId ?? undefined, confirmedBy: "user", ts: Date.now() }),
      }, ["☑ Bevestig voorstel (punt aan camera-keuze)"]),
    );
  }
  if (pendingSwitch > 0 && st.phase !== "match_over") {
    extras.push(
      h("button", {
        class: "btn warn",
        onclick: () => app.emit({ type: "SIDE_SWITCH_CONFIRMED", ts: Date.now() }),
      }, [`⇄ Kantwissel bevestigen (${pendingSwitch})`]),
    );
  }
  extras.push(
    h("button", { class: "btn", onclick: () => app.emit(undoEv()) }, ["↩ Ongedaan maken"]),
    h("button", {
      class: "btn ghost",
      onclick: () => app.emit({ type: "SIDES_SWAP_MANUAL", ts: Date.now() }),
      title: "Zijde-correctie zonder wedstrijdstaat te verliezen",
    }, ["⇄ Zijden omwisselen (correctie)"]),
  );

  if (st.phase === "set_over" && !st.matchWinner) {
    extras.push(
      h("button", {
        class: "btn primary big",
        onclick: () => openSetConfirm(app, st.setNumber + 1, () => app.render()),
      }, [`▶ Start set ${st.setNumber + 1}`]),
    );
  }
  if (st.phase === "match_over") {
    extras.push(
      h("button", {
        class: "btn primary big",
        onclick: () => app.go("result"),
      }, ["🏁 Naar resultaat"]),
    );
  }

  const navRow = h("div", { class: "topnav" }, [
    h("button", { class: "btn small ghost", onclick: () => app.go("camera") }, ["📷 Camera"]),
    h("span", { class: "muted small" }, [setInfo]),
    h("button", { class: "btn small ghost", onclick: () => app.go("result") }, ["Log"]),
  ]);

  const debugToggle = h("button", { class: "btn small ghost", onclick: () => { const panel = root.querySelector(".debug"); if (panel) (panel as HTMLDetailsElement).open = !(panel as HTMLDetailsElement).open; } }, ["🛠 Debug"]);

  root.append(
    navRow,
    scoreEl,
    h("div", { class: "status-row" }, [statusChip, debugToggle]),
    h("div", { class: "detail-row" }, [serverLine, switchLine]),
    h("div", { class: "controls" }, [...buttons, h("div", { class: "btn-row small-btns" }, extras)]),
    buildDebugPanel(app),
  );

  function teamRow(s: MatchState, team: "A" | "B"): HTMLElement {
    const p = s.currentSet.points[team];
    const t = teams[team];
    const setsWon = s.setsWon[team];
    const wonDots = Array.from({ length: Math.max(setsWon, 0) }, () => "●").join("");
    return h("div", { class: `team-row team-${team}` }, [
      h("div", { class: "team-name" }, [t.name]),
      h("div", { class: "team-players small" }, [`${t.players[0]} · ${t.players[1]}`]),
      h("div", { class: "sets-won" }, [wonDots || "—"]),
      h("div", { class: `score-small p-${team}` }, [String(p)]),
    ]);
  }

  function pointBtn(a: App, team: "A" | "B"): HTMLElement {
    return h(
      "button",
      {
        class: `btn point point-${team}`,
        disabled: a.engine.state.phase === "match_over",
        onclick: () => a.emit(manualEv(team)),
      },
      [`PUNT ${teams[team].name}`],
    );
  }
}
